import { PropsWithChildren, Fragment, ReactNode } from 'react'

import {
  useFloating,
  useInteractions,
  FloatingPortal,
  FloatingOverlay,
  useClick,
  useRole,
  useDismiss,
} from '@floating-ui/react'

import { XMark } from '@erudilabs/icons'

import { mergeClassName } from '@erudilabs/merge-class-utils'

export interface ModalProps extends PropsWithChildren {
  testId?: string
  className?: string
  closeIcon?: boolean
  heading?: string
  subheading?: string
  action?: ReactNode
  open: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: (value: any) => void
}

export const Modal = (props: ModalProps) => {
  const {
    testId,
    className,
    children,
    open,
    subheading,
    onClose,
    heading,
    action,
  } = props

  const { refs, context } = useFloating({
    open: open,
    onOpenChange: onClose,
  })
  const click = useClick(context, { enabled: true })
  const dismiss = useDismiss(context)
  const role = useRole(context)

  const { getFloatingProps } = useInteractions([click, dismiss, role])

  const showHeader = heading || onClose || subheading

  return (
    <div data-testid={testId} className={mergeClassName(className)}>
      {open && (
        <FloatingPortal>
          <FloatingOverlay
            className="z-50 bg-black bg-opacity-70 backdrop-blur-sm grid place-items-end md:place-items-center p-0 md:p-4 h-[100svh]"
            lockScroll
          >
            <div
              ref={refs.setFloating}
              className={mergeClassName([
                'text-left text-sm transition-opacity border duration-300 rounded-t-xl md:rounded-xl w-full md:max-w-[500px] shadow-sm overflow-hidden',
                'bg-white text-gray-900 before:bg-white border-gray-200',
                'dark:bg-gray-900 dark:text-gray-100 dark:before:bg-black dark:border-gray-800',
              ])}
              {...getFloatingProps()}
            >
              <Fragment>
                <div className={mergeClassName('relative')}>
                  {showHeader && (
                    <div className="px-5 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 min-h-[50px]">
                      {heading && (
                        <h5 className="line-clamp-1 pr-5">{heading}</h5>
                      )}
                      {subheading && (
                        <p className="text-gray-700 mt-1 pr-5">{subheading}</p>
                      )}
                      {onClose && (
                        <XMark
                          className="absolute top-3 right-4 text-red-600 cursor-pointer w-6 h-6 dark:text-red-400"
                          onClick={onClose}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="max-h-[calc(100svh-200px)] md:max-h-[600px] px-5 py-5 overflow-y-auto overflow-x-hidden">
                  {children}
                </div>
                {action && (
                  <div className="py-4 border-t border-gray-200 dark:border-gray-800">
                    <div className="px-5">{action}</div>
                  </div>
                )}
              </Fragment>
            </div>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </div>
  )
}
